import type { PlayerIndex, ScoreState } from '@aiscoreboard/contracts';
import { useT } from '../lib/i18n';

interface Props {
  state: ScoreState;
  onNextFrame: () => void;
  onNewMatch: () => void;
  onResume: () => void;
  saving: boolean;
  error: string | null;
}

/**
 * Shown between frames and at the end of a match.
 *
 * It blocks the pad on purpose: potting into a finished frame is the easiest
 * way for an operator to corrupt the score, and undo is the only way back.
 */
export function FrameOverlay({ state, onNextFrame, onNewMatch, onResume, saving, error }: Props) {
  const t = useT();

  if (state.status !== 'frame_over' && state.status !== 'match_over') return null;

  const last = state.frameResults[state.frameResults.length - 1];
  const matchOver = state.status === 'match_over';
  const winner: PlayerIndex | null = matchOver ? state.matchWinner : (last?.winner ?? null);

  if (state.idleClosure) {
    return (
      <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
        <section role="dialog" aria-modal="true" aria-labelledby="saved-match-title"
          className="panel max-h-[calc(100dvh-2rem)] w-full max-w-md space-y-5 overflow-y-auto text-center">
          <h2 id="saved-match-title" className="text-2xl font-bold">{t('frame.savedTitle')}</h2>
          <p className="text-sm text-white/70">{t('frame.savedHint')}</p>
          <div className="grid grid-cols-2 gap-3">
            {([0, 1] as const).map(player => (
              <div key={player} className="min-w-0">
                <div className="break-words text-white/80">{state.players[player]}</div>
                <div className="num text-4xl">{state.scores[player]}</div>
              </div>
            ))}
          </div>
          <p className="num text-white/70">
            {t('frame.savedFrames', { n: state.frameNumber, a: state.framesWon[0], b: state.framesWon[1] })}
          </p>
          <p className="text-xs text-white/50">{t('frame.resumeOffline')}</p>
          {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
          <button type="button" onClick={onResume} disabled={saving}
            className="btn w-full bg-white text-hud-bg disabled:opacity-50">
            {saving ? t('match.saving') : t('frame.resumeMatch')}
          </button>
          <button type="button" onClick={onNewMatch} disabled={saving} className="btn-ghost w-full">
            {t('frame.newMatch')}
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="panel max-h-[calc(100dvh-2rem)] w-full max-w-md space-y-5 overflow-y-auto text-center">
        <div className="text-xs uppercase tracking-[0.2em] text-white/50">
          {matchOver
            ? t('frame.matchOver')
            : t('frame.over', { n: last?.frameNumber ?? state.frameNumber })}
        </div>

        {winner !== null && (
          <div className="min-h-[4.5rem] break-words text-3xl font-black">
            {matchOver
              ? t('frame.winsMatch', { name: state.players[winner] })
              : t('frame.takesFrame', { name: state.players[winner] })}
          </div>
        )}

        {last && (
          <div className="text-white/70 num tabular-nums">
            {t('frame.result', {
              n: last.frameNumber,
              a: last.scores[0],
              b: last.scores[1],
            })}
          </div>
        )}

        <div className="flex items-center justify-center gap-3 text-4xl num-lg">
          <span className={winner === 0 ? '' : 'text-white/40'}>{state.framesWon[0]}</span>
          <span className="text-lg text-white/40">{t('frame.framesWord')}</span>
          <span className={winner === 1 ? '' : 'text-white/40'}>{state.framesWon[1]}</span>
        </div>

        <div className="text-xs uppercase tracking-wider text-white/40">
          {t('frame.summary', { n: state.bestOf, m: state.framesToWin })}
        </div>

        {state.frameResults.length > 1 && (
          <ul className="max-h-32 space-y-1 overflow-y-auto text-sm text-white/60">
            {state.frameResults.map((r) => (
              <li key={r.frameNumber} className="num tabular-nums">
                F{r.frameNumber}: {r.scores[0]}&ndash;{r.scores[1]} ({state.players[r.winner]})
              </li>
            ))}
          </ul>
        )}

        {matchOver ? (
          <button type="button" onClick={onNewMatch} className="btn w-full bg-white text-hud-bg">
            {t('frame.newMatch')}
          </button>
        ) : (
          <button type="button" onClick={onNextFrame} className="btn w-full bg-white text-hud-bg">
            {t('frame.startNext', { n: state.frameNumber + 1 })}
          </button>
        )}
      </div>
    </div>
  );
}
